import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { extracEmailFromUser, validateUsermail } from '@/utils/helpers';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import StyledButton from '../StyledHtmlElements/Button';
import { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useCognito } from '@/hooks/use-cognito';
import { Plus } from 'lucide-react';
import SelectUserGroup from '../SelectUserGroup';
import { Checkbox } from '../ui/checkbox';

type Props = {
  teamName: string;
  isGlobalAdmin: boolean;
  isGroupAdmin: boolean;
};

const CreateUserForm = ({ teamName, isGlobalAdmin, isGroupAdmin }: Props) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const { groupList, userList, createUser, addUserToGroup, errorMesssage, loading } = useCognito({
    errorHandler: displayErrorToast,
  });

  function displayErrorToast(err: string) {
    if (open) {
      toast({
        title: 'Fehler',
        description: err,
        variant: 'destructive',
        duration: 5000,
      });
    }
  }

  const formSchema = z
    .object({
      userMail: z
        .string()
        .refine(validateUsermail, {
          message: 'Ungültige E-Mail-Adresse',
        })
        .refine(
          (email) => {
            const emailExists = userList?.map((user) => extracEmailFromUser(user)).includes(email);
            return !emailExists;
          },
          {
            message: 'E-Mail-Adresse existiert bereits',
          }
        ),
      groups: z
        .array(z.string())
        .refine((groups) => groups.filter((group) => !group.toLowerCase().includes('admin')).length, {
          message: 'Bitte wähle mindestens einen Verein',
        }),

      scoutAccess: z.boolean(),
      clubAccess: z.boolean(),
    })
    .refine((data) => data.scoutAccess || data.clubAccess, {
      message: 'Mindestens eine Zugriffsoption muss aktiviert sein',
      path: ['clubAccess'],
    });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      userMail: '',
      groups: [],
      scoutAccess: false,
      clubAccess: false,
    },
  });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const { userMail, groups, scoutAccess, clubAccess } = data;

    await createUser(userMail, scoutAccess, clubAccess);

    for (const group of groups) {
      await addUserToGroup(userMail, group);
    }

    if (!errorMesssage) {
      setOpen(false);
    }
  };

  const newUserIsAdmin = (groups: string[] = form.getValues('groups')) => {
    return groups.some((group) => group.toLowerCase().includes('admin'));
  };

  useEffect(() => {
    //clear form on close
    if (!open) {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <div className="flex mb-4">
        <DropdownMenuTrigger className="focus:outline-none focus:ring-0">
          <StyledButton variant={'outline'}>
            <p className="flex gap-1 items-center">
              <span>
                <Plus />
              </span>
              Benutzer Erstellen
            </p>
          </StyledButton>
        </DropdownMenuTrigger>
      </div>

      <DropdownMenuContent align="start" className="min-w-[265px]">
        <DropdownMenuLabel className="sr-only">
          <h3 className="font-normal text-lg ">Benutzer erstellen</h3>
          <DropdownMenuSeparator />
        </DropdownMenuLabel>

        {/* User Email */}
        <>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-4">
              {/* Usermail */}
              <FormField
                control={form.control}
                name="userMail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <p className="font-heading tracking-wide text-base">E-Mail</p>
                    </FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="user@email.de" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* User Groups */}
              <FormField
                control={form.control}
                name="groups"
                render={() => (
                  <FormItem className="space-y-0">
                    <FormLabel className="font-heading tracking-wide text-base">Vereinsoptionen</FormLabel>
                    <FormControl>
                      <div className="p-2">
                        <SelectUserGroup
                          userGroups={isGlobalAdmin ? [] : [teamName]}
                          disabled={!form.getValues('groups').length}
                          allGroups={groupList}
                          onChange={(newGroups) => {
                            if (newGroups.length) {
                              form.setValue('groups', newGroups);
                              form.clearErrors('groups');

                              form.trigger('groups');

                              // set automatically scout and club access if user is admin. Uncheck if user is not admin
                              form.setValue('scoutAccess', newUserIsAdmin(newGroups));
                              form.setValue('clubAccess', newUserIsAdmin(newGroups));

                              form.trigger('scoutAccess');
                              form.trigger('clubAccess');
                            }
                          }}
                          teamName={teamName}
                          isGlobalAdmin={isGlobalAdmin}
                          isGroupAdmin={isGroupAdmin}
                          showSelectedGroup={isGlobalAdmin}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-col">
                <FormLabel className="font-heading tracking-wide text-base">Zugriffsoptionen</FormLabel>

                <div className="flex flex-col gap-2 p-2">
                  <FormField
                    control={form.control}
                    name="scoutAccess"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="checkbox-scout"
                              checked={field.value}
                              onCheckedChange={(value) => {
                                field.onChange(value);
                                form.trigger('scoutAccess');
                              }}
                              disabled={newUserIsAdmin()}
                            />
                            <label
                              htmlFor="checkbox-scout"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                              Scouting
                            </label>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="clubAccess"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="checkbox-scout"
                              checked={field.value}
                              onCheckedChange={(value) => {
                                field.onChange(value);
                                form.trigger('clubAccess');
                              }}
                              disabled={newUserIsAdmin()}
                            />
                            <label
                              htmlFor="checkbox-scout"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                              Club Controlling
                            </label>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <StyledButton className="w-full" type="submit" loading={loading}>
                Erstellen
              </StyledButton>
            </form>
          </Form>
        </>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default CreateUserForm;
